import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Coins,
  Filter,
  Gift,
  Globe,
  Mail,
  Eye,
  Megaphone,
  MessageCircle,
  Pencil,
  MessageSquare,
  MoreVertical,
  Plus,
  Search,
  Settings,
  Smartphone,
  Users,
  BarChart3,
  Zap,
} from "lucide-react";
import {
  CampaignsApi,
  campaignKeys,
  isCampaignEditable,
} from "@/api/perkpoint/campaigns";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { useActiveProgramId } from "../programs/program-scope";
import {
  campaignRowFromApi,
  type CampaignRow,
  type CampaignStatus,
  type CampaignType,
  type Channel,
} from "./campaign-types";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ChartExportButton, StartEndDates } from "../chart-controls";
import { defaultDateWindow } from "../chart-dates";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { CommunicationSetting } from "../communication/CommunicationSetting";
import { CampaignDetailsSheet } from "./CampaignDetailsSheet";
import { hasResults } from "./campaign-performance/performance";
import { toast } from "sonner";

type CampaignTab = "all" | "upcoming" | "ongoing" | "ended" | "drafts";

export type {
  CampaignRow,
  CampaignStatus,
  CampaignType,
  Channel,
} from "./campaign-types";
export { campaignRowFromApi } from "./campaign-types";

const TABS: { id: CampaignTab; label: string }[] = [
  { id: "all", label: "All campaigns" },
  { id: "upcoming", label: "Upcoming" },
  { id: "ongoing", label: "Ongoing" },
  { id: "ended", label: "Ended" },
  { id: "drafts", label: "Drafts" },
];

const performanceData = [
  { month: "Jan", issued: 180000, redeemed: 120000 },
  { month: "Feb", issued: 200000, redeemed: 140000 },
  { month: "Mar", issued: 220000, redeemed: 150000 },
  { month: "Apr", issued: 210000, redeemed: 160000 },
  { month: "May", issued: 240000, redeemed: 170000 },
  { month: "Jun", issued: 260000, redeemed: 190000 },
  { month: "Jul", issued: 250000, redeemed: 180000 },
  { month: "Aug", issued: 270000, redeemed: 200000 },
  { month: "Sep", issued: 280000, redeemed: 210000 },
  { month: "Oct", issued: 290000, redeemed: 220000 },
  { month: "Nov", issued: 275000, redeemed: 205000 },
  { month: "Dec", issued: 300000, redeemed: 230000 },
];

const channelPerformance = [
  {
    name: "WhatsApp",
    value: "820K",
    percent: 34,
    width: "100%",
    icon: MessageCircle,
  },
  {
    name: "Email",
    value: "640K",
    percent: 27,
    width: "79%",
    icon: Mail,
  },
  {
    name: "SMS",
    value: "480K",
    percent: 20,
    width: "59%",
    icon: MessageSquare,
  },
  {
    name: "In-app",
    value: "320K",
    percent: 13,
    width: "38%",
    icon: Smartphone,
  },
  {
    name: "Web",
    value: "160K",
    percent: 6,
    width: "18%",
    icon: Globe,
  },
];

export const campaigns: CampaignRow[] = [
  {
    id: "1",
    code: "CP-001",
    name: "Weekend Bonus",
    description: "Extra points on weekends",
    type: "Promotional",
    channels: ["WhatsApp", "Email"],
    bonusPoints: "2x points",
    startDate: "Jan 10, 2025",
    endDate: "Dec 31, 2025",
    status: "Ongoing",
    membersReached: "42.6K",
    pointsIssued: "680K",
    messages: "58.2K",
  },
  {
    id: "2",
    code: "CP-002",
    name: "Welcome Series",
    description: "Onboarding rewards for new members",
    type: "Lifecycle",
    channels: ["Email", "SMS"],
    bonusPoints: "1,000 pts",
    startDate: "Feb 01, 2025",
    endDate: "Dec 31, 2025",
    status: "Ongoing",
    membersReached: "18.4K",
    pointsIssued: "220K",
    messages: "24.1K",
  },
  {
    id: "3",
    code: "CP-003",
    name: "Refer a Friend",
    description: "Bonus for successful referrals",
    type: "Referral",
    channels: ["WhatsApp", "In-app"],
    bonusPoints: "500 pts",
    startDate: "Mar 15, 2025",
    endDate: "Jun 15, 2025",
    status: "Ended",
    membersReached: "9.8K",
    pointsIssued: "146K",
    messages: "12.4K",
  },
  {
    id: "4",
    code: "CP-004",
    name: "Festival Rewards",
    description: "Seasonal campaign for festive period",
    type: "Seasonal",
    channels: ["WhatsApp", "Email", "SMS"],
    bonusPoints: "1.5x points",
    startDate: "Oct 20, 2025",
    endDate: "Nov 15, 2025",
    status: "Scheduled",
    membersReached: "0",
    pointsIssued: "0",
    messages: "0",
  },
  {
    id: "5",
    code: "CP-005",
    name: "Gold Tier Boost",
    description: "Exclusive boost for Gold members",
    type: "Tier",
    channels: ["Email", "In-app"],
    bonusPoints: "750 pts",
    startDate: "Apr 01, 2025",
    endDate: "Apr 30, 2025",
    status: "Ended",
    membersReached: "6.2K",
    pointsIssued: "98K",
    messages: "7.8K",
  },
  {
    id: "6",
    code: "CP-006",
    name: "Birthday Surprise",
    description: "Draft campaign for birthday rewards",
    type: "Lifecycle",
    channels: ["Email"],
    bonusPoints: "300 pts",
    startDate: "—",
    endDate: "—",
    status: "Draft",
    membersReached: "0",
    pointsIssued: "0",
    messages: "0",
  },
];

function formatKpiCount(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (Math.abs(n) >= 10_000) {
    return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }
  return n.toLocaleString("en-IN");
}

function TypeBadge({ type }: { type: CampaignType }) {
  const styles: Record<CampaignType, string> = {
    Promotional: "bg-orange-50 text-orange-700",
    Lifecycle: "bg-sky-50 text-sky-700",
    Referral: "bg-violet-50 text-violet-700",
    Seasonal: "bg-amber-50 text-amber-700",
    Tier: "bg-indigo-50 text-indigo-700",
  };

  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
        styles[type],
      )}
    >
      {type}
    </span>
  );
}

function StatusBadge({ status }: { status: CampaignStatus }) {
  const styles: Record<CampaignStatus, { text: string; dot: string }> = {
    Ongoing: { text: "text-green-700", dot: "bg-green-500" },
    Scheduled: { text: "text-sky-700", dot: "bg-sky-500" },
    "Pending approval": { text: "text-amber-700", dot: "bg-amber-500" },
    Ended: { text: "text-slate-600", dot: "bg-slate-400" },
    Draft: { text: "text-slate-700", dot: "bg-slate-500" },
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-sm",
        styles[status].text,
      )}
    >
      <span className={cn("h-2 w-2 rounded-full", styles[status].dot)} />
      {status}
    </span>
  );
}

export default function RewardsCampaigns() {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<CampaignTab>("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const [channelOpen, setChannelOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsCampaign, setDetailsCampaign] = useState<CampaignRow | null>(
    null,
  );
  const initialRange = defaultDateWindow(30);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const { programId } = useActiveProgramId();

  const openCampaignDetails = (campaign: CampaignRow) => {
    setDetailsCampaign(campaign);
    setDetailsOpen(true);
  };

  const campaignsListQuery = useQuery({
    queryKey: campaignKeys.list(programId ?? "", { pageSize: 100 }),
    queryFn: () => CampaignsApi.list(programId!, { pageSize: 100 }),
    enabled: Boolean(programId),
  });

  const campaignsSummaryQuery = useQuery({
    queryKey: campaignKeys.summary(programId ?? ""),
    queryFn: () => CampaignsApi.summary(programId!),
    enabled: Boolean(programId),
  });
  const summary = campaignsSummaryQuery.data;

  const apiCampaignRows = useMemo(
    () => (campaignsListQuery.data?.items ?? []).map(campaignRowFromApi),
    [campaignsListQuery.data?.items],
  );

  const filteredCampaigns = useMemo(() => {
    const query = search.trim().toLowerCase();
    return apiCampaignRows.filter((campaign) => {
      const matchesSearch =
        !query ||
        campaign.name.toLowerCase().includes(query) ||
        campaign.code.toLowerCase().includes(query) ||
        campaign.description.toLowerCase().includes(query);

      const matchesTab =
        activeTab === "all" ||
        (activeTab === "upcoming" &&
          (campaign.status === "Scheduled" ||
            campaign.status === "Pending approval")) ||
        (activeTab === "ongoing" && campaign.status === "Ongoing") ||
        (activeTab === "ended" && campaign.status === "Ended") ||
        (activeTab === "drafts" && campaign.status === "Draft");

      return matchesSearch && matchesTab;
    });
  }, [search, activeTab, apiCampaignRows]);

  const allVisibleSelected =
    filteredCampaigns.length > 0 &&
    filteredCampaigns.every((c) => selectedIds.includes(c.id));

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? filteredCampaigns.map((c) => c.id) : []);
  };

  const toggleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((value) => value !== id),
    );
  };

  useEffect(() => {
    if (campaignsListQuery.isError) {
      toast.error(
        apiErrorMessage(campaignsListQuery.error, "Could not load campaigns"),
      );
    }
  }, [campaignsListQuery.isError, campaignsListQuery.error]);

  return (
    <div className="space-y-6 pb-2">
      {/* Title */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Campaigns
          </h1>
          <p className="mt-1 text-muted-foreground">
            Create and manage campaigns to drive engagement and reward your
            members.
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center justify-end gap-2 lg:w-auto">
          <StartEndDates from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <Button
            type="button"
            variant="outline"
            className="bg-white"
            onClick={() => setChannelOpen(true)}
          >
            <Settings className="mr-1.5 h-4 w-4" />
            Configure channel
          </Button>
          <Button
            type="button"
            className="bg-green-600 text-white hover:bg-green-700"
            onClick={() => navigate("/perkpoint/rewards/campaigns/create")}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Create campaign
          </Button>
        </div>
      </div>

      {/* KPI cards — values from campaigns/summary; change % skipped for now */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {(
          [
            {
              label: "Total campaigns",
              value: formatKpiCount(summary?.total_campaigns),
              icon: Megaphone,
            },
            {
              label: "Active campaigns",
              value: formatKpiCount(summary?.active_campaigns),
              icon: Zap,
            },
            {
              label: "Members reached",
              value: formatKpiCount(summary?.members_reached),
              icon: Users,
            },
            {
              label: "Points issued",
              value: formatKpiCount(summary?.points_issued),
              icon: Coins,
            },
            {
              label: "Redemptions",
              value: formatKpiCount(summary?.redemptions),
              icon: Gift,
            },
          ] as const
        ).map((kpi) => (
          <Card key={kpi.label} className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <kpi.icon className="h-5 w-5" />
              </div>
              <p className="mt-4 text-sm text-muted-foreground">{kpi.label}</p>
              <p className="mt-1 text-3xl font-bold text-slate-900">
                {campaignsSummaryQuery.isLoading ? "…" : kpi.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid gap-4 xl:grid-cols-5">
        <Card className="border-slate-200 shadow-sm xl:col-span-3">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Campaign performance
                </h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Points issued vs redeemed over time
                </p>
              </div>
              <ChartExportButton
                filename="campaign-performance.csv"
                rows={performanceData.map((row) => ({
                  Month: row.month,
                  "Points issued": row.issued,
                  "Points redeemed": row.redeemed,
                }))}
              />
            </div>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <YAxis
                    tick={{ fill: "#64748b", fontSize: 12 }}
                    tickFormatter={(value) =>
                      value >= 1000 ? `${Math.round(value / 1000)}K` : `${value}`
                    }
                  />
                  <Tooltip
                    formatter={(value: number) =>
                      `${value.toLocaleString("en-IN")} pts`
                    }
                  />
                  <Legend />
                  <Bar
                    dataKey="issued"
                    name="Points issued"
                    fill="#15803d"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="redeemed"
                    name="Points redeemed"
                    fill="#86efac"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm xl:col-span-2">
          <CardContent className="p-5">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Channel performance
                </h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Points issued by channel
                </p>
              </div>
              <ChartExportButton
                filename="campaign-channels.csv"
                rows={channelPerformance.map((channel) => ({
                  Channel: channel.name,
                  "Points issued": channel.value,
                  Share: channel.percent,
                }))}
              />
            </div>
            <div className="space-y-4">
              {channelPerformance.map((channel) => (
                <div key={channel.name}>
                  <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                    <div className="flex items-center gap-2 font-medium text-slate-700">
                      <channel.icon className="h-4 w-4 text-slate-500" />
                      {channel.name}
                    </div>
                    <span className="text-slate-700">
                      <span className="font-semibold text-slate-900">
                        {channel.value}
                      </span>{" "}
                      <span className="text-muted-foreground">
                        ({channel.percent}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-green-600"
                      style={{ width: channel.width }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="space-y-4 p-5 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id);
                    setPage(1);
                  }}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                    activeTab === tab.id
                      ? "border-green-600 bg-green-50 text-green-700"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative min-w-[240px] flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search campaigns by name or ID..."
                  className="h-10 pl-9"
                />
              </div>
              <Button type="button" variant="outline" className="h-10 bg-white">
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
            </div>
          </div>

          <div className="w-full rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(checked) =>
                        toggleSelectAll(Boolean(checked))
                      }
                      aria-label="Select all campaigns"
                    />
                  </TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead className="hidden sm:table-cell">Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Dates</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {!programId ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      Select a program to load campaigns.
                    </TableCell>
                  </TableRow>
                ) : campaignsListQuery.isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      Loading campaigns…
                    </TableCell>
                  </TableRow>
                ) : filteredCampaigns.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      {campaignsListQuery.isError
                        ? "Could not load campaigns."
                        : "No campaigns match the selected filters."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCampaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(campaign.id)}
                          onCheckedChange={(checked) =>
                            toggleSelectOne(campaign.id, Boolean(checked))
                          }
                          aria-label={`Select ${campaign.name}`}
                        />
                      </TableCell>
                      <TableCell className="min-w-0 max-w-[280px] sm:max-w-none">
                        <button
                          type="button"
                          className="block w-full truncate text-left"
                          onClick={() => openCampaignDetails(campaign)}
                        >
                          <span className="block truncate font-semibold text-slate-900 hover:text-green-700">
                            {campaign.name}
                          </span>
                          <span className="block truncate text-sm text-muted-foreground">
                            {campaign.code}
                          </span>
                        </button>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <TypeBadge type={campaign.type} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={campaign.status} />
                      </TableCell>
                      <TableCell className="hidden text-sm text-slate-600 md:table-cell">
                        {campaign.startDate}
                        {campaign.endDate !== "—" ? (
                          <span className="text-muted-foreground">
                            {" "}
                            – {campaign.endDate}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => openCampaignDetails(campaign)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View details
                            </DropdownMenuItem>
                            {hasResults(campaign.status) ? (
                              <DropdownMenuItem asChild>
                                <Link
                                  to={`/perkpoint/rewards/campaigns/${campaign.id}/performance`}
                                >
                                  <BarChart3 className="mr-2 h-4 w-4" />
                                  View performance
                                </Link>
                              </DropdownMenuItem>
                            ) : null}
                            {isCampaignEditable(campaign.apiStatus) ? (
                              <DropdownMenuItem asChild>
                                <Link
                                  to={`/perkpoint/rewards/campaigns/${campaign.id}/edit`}
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit
                                </Link>
                              </DropdownMenuItem>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Showing 1-{filteredCampaigns.length} of {filteredCampaigns.length}{" "}
              campaigns
            </p>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                className="h-8 w-8 bg-green-600 text-white hover:bg-green-700"
              >
                1
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <CampaignDetailsSheet
        open={detailsOpen}
        onOpenChange={(open) => {
          setDetailsOpen(open);
          if (!open) {
            setDetailsCampaign(null);
            void campaignsListQuery.refetch();
            void campaignsSummaryQuery.refetch();
          }
        }}
        programId={programId}
        campaignId={detailsCampaign?.id ?? null}
        summary={detailsCampaign}
      />

      <Sheet open={channelOpen} onOpenChange={setChannelOpen}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-3xl"
        >
          <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">
            Configure channel
          </SheetTitle>
          <SheetDescription className="mt-1 text-sm text-muted-foreground">
            Set up email, SMS, and WhatsApp for campaign messages.
          </SheetDescription>
          <div className="mt-6">
            <CommunicationSetting />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
